// letterForms.js
//
// Helpers for the four positional shapes of a Dari/Perso-Arabic letter.
//
// We don't store glyph images. Instead we coax the font into rendering each
// positional shape using the Zero-Width Joiner (U+200D): putting a ZWJ on a
// side tells the shaper "there is a connected letter here", so the glyph takes
// its initial / medial / final form.

import { dariAlphabet } from './dariAlphabet';

const ZWJ = '‍';

// Letters that never connect to the *following* letter. They therefore only
// ever appear in isolated or final shapes.
export const NON_JOINERS = new Set(
  dariAlphabet.filter((l) => l.joins === false).map((l) => l.dari)
);

export function isJoiner(letter) {
  return !NON_JOINERS.has(letter);
}

// Render a single letter in a given positional shape, using ZWJ.
// position: 'isolated' | 'initial' | 'medial' | 'final'
export function shapeOf(letter, position) {
  switch (position) {
    case 'initial':
      return letter + ZWJ;
    case 'medial':
      return ZWJ + letter + ZWJ;
    case 'final':
      return ZWJ + letter;
    case 'isolated':
    default:
      return letter;
  }
}

// Which positional shapes a letter actually has. Non-joiners only take
// isolated + final (they connect on their right side, to a previous letter).
export function formsFor(letter) {
  return isJoiner(letter)
    ? ['isolated', 'initial', 'medial', 'final']
    : ['isolated', 'final'];
}

// Classify the positional shape of the letter at `index` within `word`,
// using the standard Arabic joining algorithm:
//   - connects to the previous letter iff a previous letter exists and it is
//     a joiner (non-joiners don't connect forward to the next letter);
//   - connects to the next letter iff this letter is a joiner and a next
//     letter exists.
export function positionAt(word, index) {
  const letters = Array.from(word);
  const cur = letters[index];
  const prev = letters[index - 1];
  const next = letters[index + 1];

  const connectsPrev = prev !== undefined && isJoiner(prev);
  const connectsNext = next !== undefined && isJoiner(cur);

  if (connectsPrev && connectsNext) return 'medial';
  if (!connectsPrev && connectsNext) return 'initial';
  if (connectsPrev && !connectsNext) return 'final';
  return 'isolated';
}

export const POSITION_LABELS = {
  isolated: 'Isolated',
  initial: 'Initial',
  medial: 'Medial',
  final: 'Final'
};
